import {Injectable} from '@angular/core';
import {Action, createSelector, State, StateContext} from '@ngxs/store';
import {ClearEntitlements, FetchEntitlementForResource} from './entitlements.actions';
import {catchError, forkJoin, map, Observable, of, tap} from 'rxjs';
import {ClientPaymentsApiService} from '@tisa-group/core/api/wicket/client-payments-api/client-payments-api.service';

export type EntitlementsStateModel = Record<string, boolean>;

@State<EntitlementsStateModel>({
  name: 'Entitlements',
  defaults: {},
})
@Injectable()
export class EntitlementsState {
  @Action(FetchEntitlementForResource)
  loadEntitlements(
    ctx: StateContext<EntitlementsStateModel>,
    {resources}: FetchEntitlementForResource,
  ): Observable<boolean> | boolean {
    const state = ctx.getState();

    const uniqueResources = [...new Set(resources)];

    return forkJoin(
      uniqueResources.map(resource =>
        state[resource] !== undefined
          ? of(state[resource])
          : this.paymentService.entitlementsInquiryForToday(resource).pipe(
              tap({
                next: () => {
                  ctx.patchState({[resource]: true});
                },
                error: () => {
                  ctx.patchState({[resource]: false});
                },
              }),
              catchError(() => of(false)),
            ),
      ),
    ).pipe(map(results => results.every(result => !!result)));
  }

  @Action(ClearEntitlements)
  clearEntitlements(ctx: StateContext<EntitlementsStateModel>): void {
    ctx.setState({});
  }

  static getEntitlementForResources(resources: string[]) {
    return createSelector<(state: EntitlementsStateModel) => boolean | undefined>(
      [EntitlementsState],
      state => {
        if (resources.some(resource => state[resource] === undefined)) {
          return undefined;
        }

        return resources.every(resource => state[resource]);
      },
    );
  }

  constructor(readonly paymentService: ClientPaymentsApiService) {}
}
